import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronLeft, CheckCircle, MapPin } from 'lucide-react'
import { CATEGORIES, STANCE_QUESTIONS, saveUserAnswers, loadUserAnswers } from '../engine/matchEngine'
import { saveLocation } from '../engine/districtSelection'
import DistrictPicker from '../components/DistrictPicker'
import { useAuth } from '../contexts/AuthContext'


const PHASE_LABELS = ['Your Location', 'Rate Your Priorities', 'Share Your Stances', 'Complete!']

function WeightSlider({ category, value, onChange }) {
  return (
    <div className="card">
      <div className="flex justify-between items-center mb-2">
        <label className="font-medium text-civitas-navy">{category.label}</label>
        <span className="badge bg-civitas-light text-civitas-blue font-semibold w-8 text-center">{value}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-civitas-silver w-12">Low</span>
        <input type="range" min={1} max={10} step={1} value={value}
          onChange={e => onChange(category.id, Number(e.target.value))}
          className="flex-1 accent-civitas-navy" />
        <span className="text-xs text-civitas-silver w-12 text-right">High</span>
      </div>
    </div>
  )
}

function StanceSlider({ question, value, onChange }) {
  const label = value === 0 ? 'Neutral' : value > 0 ? `Support +${value}` : `Oppose ${value}`
  const color = value > 0 ? 'text-blue-600' : value < 0 ? 'text-red-500' : 'text-gray-500'
  return (
    <div className="card">
      <div className="flex justify-between items-center mb-2">
        <label className="font-medium text-civitas-navy">{question.label}</label>
        <span className={`badge font-semibold text-xs ${color} bg-gray-100`}>{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-red-400 w-16">Strongly Oppose</span>
        <input type="range" min={-5} max={5} step={1} value={value}
          onChange={e => onChange(question.id, Number(e.target.value))}
          className="flex-1 accent-civitas-navy" />
        <span className="text-xs text-blue-400 w-16 text-right">Strongly Support</span>
      </div>
    </div>
  )
}

export default function Questionnaire() {
  const navigate = useNavigate()
  const { user, userPrefs, saveUserPrefs } = useAuth()
  const saved = loadUserAnswers()

  const [phase, setPhase] = useState(0)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [selectedState, setSelectedState] = useState(userPrefs?.state || saved.state || '')
  const [selectedDistrict, setSelectedDistrict] = useState(userPrefs?.district || saved.district || '')
  const [weights, setWeights] = useState(() => {
    const d = {}
    CATEGORIES.forEach(c => { d[c.id] = saved.weights?.[c.id] ?? 5 })
    return d
  })
  const [stances, setStances] = useState(() => {
    const d = {}
    STANCE_QUESTIONS.forEach(q => { d[q.id] = saved.stances?.[q.id] ?? 0 })
    return d
  })

  const progress = (phase / 3) * 100

  async function handleNext() {
    if (phase === 1) {
      setPhase(2); window.scrollTo(0, 0)
    } else if (phase === 2) {
      saveUserAnswers(weights, stances, selectedState, selectedDistrict)
      setSaving(true)
      setSaveError('')
      try {
        if (user && saveUserPrefs) {
          await saveUserPrefs(selectedState, selectedDistrict, weights, stances)
        }
        setPhase(3); window.scrollTo(0, 0)
      } catch (error) {
        setSaveError(error.message || 'Unable to save your responses. Please try again.')
      } finally {
        setSaving(false)
      }
    }
  }

  function handleBack() {
    if (phase > 0) { setPhase(phase - 1); window.scrollTo(0, 0) }
  }

  return (
    <div className="section max-w-3xl">
      <div className="mb-8">
        <h1 className="font-display text-3xl text-civitas-navy mb-2">Civic Priority Questionnaire</h1>
        <p className="text-civitas-silver">
          {phase === 0 && 'Question 1 — Where are you voting? Look up your address or choose your district manually.'}
          {phase === 1 && 'Step 2 of 3 -- Rate how important each policy area is to you.'}
          {phase === 2 && 'Step 3 of 3 -- Share your stance on specific policy questions.'}
          {phase === 3 && 'All done! Your responses are saved.'}
        </p>
      </div>

      <div className="mb-8">
        <div className="flex justify-between text-xs mb-2">
          {PHASE_LABELS.map((label, i) => (
            <span key={label} className={`font-medium ${i <= phase ? 'text-civitas-navy' : 'text-gray-300'}`}>{label}</span>
          ))}
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-2 bg-civitas-navy rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {phase === 0 && (
        <DistrictPicker embedded initialState={selectedState} initialDistrict={selectedDistrict}
          continueLabel="Next: Rate Priorities"
          onConfirm={async (state, districts) => {
            const label = saveLocation(state, districts)
            setSelectedState(state)
            setSelectedDistrict(label)
            saveUserAnswers(weights, stances, state, label)
            setSaveError('')
            setPhase(1)
            window.scrollTo(0, 0)
          }} />
      )}

      {phase === 1 && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-civitas-silver bg-civitas-light rounded-lg p-3">
            Drag each slider to rate how important this policy area is to you from 1 (low) to 10 (high).
          </p>
          {CATEGORIES.map(cat => (
            <WeightSlider key={cat.id} category={cat} value={weights[cat.id]} onChange={(id, val) => setWeights(p => ({...p, [id]: val}))} />
          ))}
        </div>
      )}

      {phase === 2 && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-civitas-silver bg-civitas-light rounded-lg p-3">
            Drag from -5 (strongly oppose) to +5 (strongly support). Leave at 0 if neutral.
          </p>
          {STANCE_QUESTIONS.map(q => (
            <StanceSlider key={q.id} question={q} value={stances[q.id]} onChange={(id, val) => setStances(p => ({...p, [id]: val}))} />
          ))}
        </div>
      )}

      {phase === 3 && (
        <div className="card text-center py-12 flex flex-col items-center gap-4">
          <CheckCircle className="h-16 w-16 text-green-500" />
          <h2 className="font-display text-2xl text-civitas-navy">Questionnaire Complete!</h2>
          <div className="flex items-center gap-2 px-4 py-2 bg-civitas-light rounded-full">
            <MapPin className="h-4 w-4 text-civitas-gold" />
            <span className="font-medium text-civitas-navy">{selectedState || 'All states'}</span>
            {selectedDistrict && <span className="text-civitas-silver">- {selectedDistrict}</span>}
          </div>
          <p className="text-civitas-silver max-w-md">
            Your responses are saved. We will show candidates for {selectedState || 'your area'} ranked by your match score.
          </p>
          <div className="flex gap-3 mt-4">
            <button onClick={() => navigate('/results')} className="btn-primary px-8">See My Matches</button>
            <button onClick={() => setPhase(0)} className="btn-secondary">Retake</button>
          </div>
        </div>
      )}

      {phase > 0 && phase < 3 && (
        <div className="mt-8">
          {saveError && (
            <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {saveError}
            </p>
          )}
          <div className="flex justify-between">
            <button onClick={handleBack} disabled={phase === 0 || saving} className="btn-secondary flex items-center gap-2 disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            <button onClick={handleNext} disabled={(phase === 0 && !selectedState) || saving} className="btn-primary flex items-center gap-2 disabled:opacity-40">
              {saving ? 'Saving...' : phase === 2 ? 'Save and See Results' : phase === 1 ? 'Next: Policy Stances' : 'Next: Rate Priorities'}
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <p className="mt-6 text-xs text-center text-civitas-silver">
        Responses stored locally in your browser. Signed-in users also save state preference to their account.
      </p>
    </div>
  )
}
